import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import withRouter from "components/utils/withRouter.js";
import styles from "styles/public.module.css";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import SnackbarConstants from "utils/constants/Snackbar.js";
import SignupService from "services/SignupService.js";
import InputErrorMessage from "components/utils/InputErrorMessage.js";
import RegexUtils from "utils/RegexUtils.js";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { getOrganizationFromHostname } from "utils/OrganizationUtils.js";
import AuthFormShell from "components/auth/AuthFormShell.js";
import AuthFormActions from "components/auth/AuthFormActions.js";

const SIGNUP_STEPS = ["Email", "Verify", "Profile"];

const SIGNUP_SUBTITLES = [
  "Enter your email to begin registration",
  "Enter the verification code sent to your email",
  "Set up your account details",
];

function PatientSignup(props) {
  const { enqueueSnackbar } = useSnackbar();
  const [signupViewState, setSignupViewState] = useState(0);
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showOtpTimer, setShowOtpTimer] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(30);
  let timer;

  useEffect(() => {
    return () => {
      stopOtpTimer();
    };
  }, []);

  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  async function handlePatientSignup(data) {
    setIsFormProcessing(true);
    if (signupViewState === 0) {
      try {
        const captchaToken = await executeRecaptcha("Register_User");
        const response = await SignupService.registerPatient(
          {
            email: data.email,
          },
          captchaToken
        );

        if (!response.data || response.data === null) {
          enqueueSnackbar(response.message, SnackbarConstants.success);
          setTimeout(() => {
            props.navigate("/account");
          }, 2000);
          setIsFormProcessing(false);
          return;
        }

        setTransactionId(response.data.transactionId);
        setSignupViewState(1);
        startOtpTimer();
        enqueueSnackbar(
          "Verify your account by entering the OTP sent to your email",
          SnackbarConstants.success
        );
      } catch (error) {
        handleError(error);
      }
    } else if (signupViewState === 1) {
      try {
        const captchaToken = await executeRecaptcha("Verify_Registered_User");
        const response = await SignupService.verifyRegisteredPatient(
          {
            email: data.email,
            otp: data.otp,
            transactionId,
          },
          captchaToken
        );
        setTransactionId(response.data.transactionId);
        setSignupViewState(2);
        enqueueSnackbar(response.message, SnackbarConstants.success);
      } catch (error) {
        handleError(error);
      }
    } else {
      try {
        const captchaToken = await executeRecaptcha("Signup_User");
        const response = await SignupService.signupPatient(
          {
            ...data,
            transactionId,
          },
          captchaToken
        );
        enqueueSnackbar(response.message, SnackbarConstants.success);
        props.navigate("/");
      } catch (error) {
        handleError(error);
      }
    }
    setIsFormProcessing(false);
  }

  function handleError(error) {
    let errorData = error.data;
    if (error.data) {
      enqueueSnackbar(errorData.message, SnackbarConstants.error);
      let errors = errorData.errors;
      if (errors) {
        errors.forEach((item, i) => {
          enqueueSnackbar(item, SnackbarConstants.error);
        });
      }
    } else {
      enqueueSnackbar("Oops! Something went wrong", SnackbarConstants.error);
    }
    setIsFormProcessing(false);
  }

  async function resendOTP() {
    setIsFormProcessing(true);
    startOtpTimer();

    try {
      const captchaToken = await executeRecaptcha("Resend_OTP");

      const data = getValues();

      const response = await SignupService.resendOTP(
        {
          email: data.email,
        },
        captchaToken
      );
      setTransactionId(response.data.transactionId);
      enqueueSnackbar("OTP resent", SnackbarConstants.success);
    } catch (error) {
      stopOtpTimer();
      handleError(error);
    }
    setIsFormProcessing(false);
  }

  function startOtpTimer() {
    setShowOtpTimer(true);
    setTimerSeconds(30);

    timer = setInterval(() => {
      setTimerSeconds((prevSeconds) => {
        if (prevSeconds == 0) {
          stopOtpTimer();
        }
        return prevSeconds - 1;
      });
    }, 1000);
  }

  function stopOtpTimer() {
    setShowOtpTimer(false);
    clearInterval(timer);
  }

  const organization = getOrganizationFromHostname();
  const brandName = organization
    ? `Melillo Center – ${organization}`
    : "Melillo Center";

  return (
    <AuthFormShell
      title="Patient registration"
      subtitle={SIGNUP_SUBTITLES[signupViewState]}
      organization={organization}
      currentStep={signupViewState}
      steps={SIGNUP_STEPS}
      footer={
        <Typography className={styles["signup-guidance"]}>
          Already have an account?{" "}
          <Link
            component="button"
            underline="hover"
            onClick={() => props.navigate("/account")}
          >
            Sign in
          </Link>
        </Typography>
      }
    >
      <form
        className={styles["auth-form"]}
        onSubmit={handleSubmit(handlePatientSignup)}
      >
        <div className="mb-3">
          <label htmlFor="emailInput" className="form-label">
            Email ID
          </label>
          <input
            type="email"
            name="email"
            className="form-control"
            id="emailInput"
            aria-describedby="emailHelp"
            placeholder="name@example.com"
            disabled={signupViewState > 0}
            {...register("email", {
              required: "Email ID is required",
              pattern: {
                value: RegexUtils.EMAIL,
                message: "Email ID is invalid",
              },
            })}
          />
          <InputErrorMessage name="email" errors={errors} />
        </div>
        {signupViewState === 0 ? (
          <div className={styles["consent-section"]}>
            <div className={styles["consent-item"]}>
              <input
                type="checkbox"
                className="form-check-input"
                id="termsAcceptedInput"
                {...register("termsAccepted", {
                  validate: (value) =>
                    value === true ||
                    "You must agree to the Terms of Service and Privacy Policy",
                })}
              />
              <label
                htmlFor="termsAcceptedInput"
                className={styles["consent-label"]}
              >
                I agree to the{" "}
                <a href="/terms-of-service" target="_blank" rel="noreferrer">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </label>
            </div>
            <InputErrorMessage name="termsAccepted" errors={errors} />
            <div className={styles["consent-item"]}>
              <input
                type="checkbox"
                className="form-check-input"
                id="smsConsentInput"
                {...register("smsConsent", {
                  validate: (value) =>
                    value === true ||
                    "You must agree to receive transactional text messages",
                })}
              />
              <label
                htmlFor="smsConsentInput"
                className={styles["consent-label"]}
              >
                I agree to receive transactional text messages from {brandName}{" "}
                regarding account verification, reminders, and other
                service-related notifications.
              </label>
            </div>
            <InputErrorMessage name="smsConsent" errors={errors} />
          </div>
        ) : null}
        {signupViewState === 1 ? (
          <>
            <div className="mb-3">
              <label htmlFor="otpInput" className="form-label">
                OTP
              </label>
              <input
                type="number"
                name="otp"
                className="form-control"
                id="otpInput"
                placeholder="Enter 6-digit code"
                {...register("otp", {
                  required: "OTP is required",
                  pattern: {
                    value: RegexUtils.OTP,
                    message: "OTP is invalid",
                  },
                })}
              />
              <InputErrorMessage name="otp" errors={errors} />
            </div>
            <div className={styles["otp-actions"]}>
              {showOtpTimer ? (
                <span className={styles["otp-timer"]}>
                  Resend OTP in {timerSeconds}s
                </span>
              ) : (
                <Link
                  component="button"
                  className={styles["support-link"]}
                  onClick={resendOTP}
                  underline="hover"
                >
                  Resend OTP
                </Link>
              )}
            </div>
          </>
        ) : null}
        {signupViewState === 2 ? (
          <div>
            <div className="mb-3">
              <label htmlFor="firstNameInput" className="form-label">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                className="form-control"
                id="firstNameInput"
                placeholder="First name"
                {...register("firstName", {
                  required: "First Name is required",
                  pattern: {
                    value: RegexUtils.NAME,
                    message: "First Name is invalid",
                  },
                })}
              />
              <InputErrorMessage name="firstName" errors={errors} />
            </div>
            <div className="mb-3">
              <label htmlFor="lastNameInput" className="form-label">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                className="form-control"
                id="lastNameInput"
                placeholder="Last name"
                {...register("lastName", {
                  required: "Last Name is required",
                  pattern: {
                    value: RegexUtils.NAME,
                    message: "Last Name is invalid",
                  },
                })}
              />
              <InputErrorMessage name="lastName" errors={errors} />
            </div>
            <div className="mb-3">
              <label htmlFor="passwordInput" className="form-label">
                Password
              </label>
              <div>
                <OutlinedInput
                  name="password"
                  id="passwordInput"
                  className={styles["password-input"]}
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((show) => !show)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...register("password", {
                    required: "Password is required",
                    pattern: {
                      value: RegexUtils.PASSWORD,
                      message: "Password is invalid",
                    },
                  })}
                />
              </div>
              <InputErrorMessage name="password" errors={errors} />
              <div id="passwordHelpBlock" className="form-text">
                At least 8 characters with one letter, one number, and one
                special character.
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="confirmPasswordInput" className="form-label">
                Confirm Password
              </label>
              <div>
                <OutlinedInput
                  name="confirmPassword"
                  id="confirmPasswordInput"
                  className={styles["password-input"]}
                  autoComplete="new-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword((show) => !show)}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...register("confirmPassword", {
                    required: "Confirm Password is required",
                    pattern: {
                      value: RegexUtils.PASSWORD,
                      message: "Confirm Password is invalid",
                    },
                  })}
                />
              </div>
              <InputErrorMessage name="confirmPassword" errors={errors} />
            </div>
          </div>
        ) : null}
        <AuthFormActions
          submitLabel={getSubmitButtonText()}
          isProcessing={isFormProcessing}
          onCancel={() => props.navigate("/account")}
        />
      </form>
    </AuthFormShell>
  );

  function getSubmitButtonText() {
    if (signupViewState === 0) return "Continue";
    if (signupViewState === 1) return "Verify";
    return "Create account";
  }
}

export default withRouter(PatientSignup);
